import { useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from "react-router";

import { useStore } from "@/app/model/RootStoreContext";
import { AmplitudeEvent, AppRoutes } from "@/shared/config";
import { initAmplitude, trackAmplitudeEvent } from "@/shared/lib";
import { ErrorFallback, Loader } from "@/shared/ui";

initAmplitude();

const HomePage = lazy(() => import("@/pages/home"));
const ChatPage = lazy(() => import("@/pages/chat"));
const VoiceChatPage = lazy(() => import("@/pages/voice-chat"));

const App = () => {
  const {
    userStore,
    deepLinkStore,
    uiStore,
    snackbarStore,
    featureToggleStore,
  } = useStore();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleDeepLink = () => {
      deepLinkStore.init({
        handleLink: (data) => {
          if (data.to_url) {
            navigate(data.to_url);
          }
        },
      });
    };

    const navigateToAuth = async () => {
      try {
        const response = await userStore.getPassportOAuthUrl();
        window.location.href = response;
      } catch (error) {
        if (error instanceof AxiosError) {
          setIsLoading(false);
          setHasError(true);
          snackbarStore.open({
            message: error.response?.data.errorText ?? error.message,
            variant: "error",
          });
        }
      }
    };

    (async () => {
      try {
        await userStore.getMe();
        setIsLoading(false);
        const redirectUrl = searchParams.get("redirect");
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          handleDeepLink();
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.status === 401) {
            navigateToAuth();
          } else {
            setIsLoading(false);
            setHasError(true);
            snackbarStore.open({
              message: error.response?.data.errorText ?? error.message,
              variant: "error",
            });
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore, deepLinkStore, snackbarStore]);

  useEffect(() => {
    uiStore.setIsMobile(isMobile);
  }, [uiStore, isMobile]);

  useEffect(() => {
    featureToggleStore.load();
  }, [featureToggleStore]);

  useEffect(() => {
    trackAmplitudeEvent(AmplitudeEvent.ChatListOpened);
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (hasError) {
    return <ErrorFallback />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path={AppRoutes.HOME} element={<HomePage />} />
        <Route path={AppRoutes.CHAT}>
          <Route index element={<Navigate to={AppRoutes.HOME} />} />
          <Route path={AppRoutes.ASSISTANT_ID} element={<ChatPage />} />
        </Route>
        <Route path={AppRoutes.VOICE_CHAT}>
          <Route index element={<Navigate to={AppRoutes.HOME} />} />
          <Route
            path={AppRoutes.VOICE_ASSISTANT_ID}
            element={<VoiceChatPage />}
          />
        </Route>
        <Route path="*" element={<Navigate to={AppRoutes.HOME} />} />
      </Routes>
    </Suspense>
  );
};

export default App;
